import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FlowService } from '../../service/FlowService';
import { useNotificationStore } from '../../store/storeNotifications';

import { useEdges, useReactFlow } from 'reactflow';

import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { FlowArrow, Gear, Info, XCircle } from '@phosphor-icons/react';
import '../../assets/styles/animatedBorder.scss';
import { FluxService } from '../../service/FluxService';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { useSessionStore } from '../../store/storeSessions';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { executeErrorHandle } from '../../util/executeErrorHandle';
import { isActiveEdge } from '../../util/findActiveHandle';
import { NodeProps } from '../../util/Types/NodeProps';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import { generateRandomId } from '../../utils/generateId';
import TagInput from '../Test/TagInput';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import Label from '../UI/Label/Label';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import OutputTextarea from '../UI/OutputTextarea/OutputTextarea';

const FlowCaller = ({ id, data, isConnectable, selected, type }: NodeProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { setNodes: setNodeState } = useReactFlow();

  const { flowId, setSaving, setSchema, flows } = useFlowsStore(state => state);
  const { setNotification } = useNotificationStore(state => state);

  const [btnLoading, setBtnLoading] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [executeCounter, setExecuteCounter] = useState(0);
  const [whoIsChanging, setWhoIsChanging] = useState({
    value: '',
    name: '',
  });

  const [hasToExecute, setHasToExecute] = useState(true);
  const [typeOCR, setTypeOCR] = useState(data?.typeOCR || 'mistral_ocr');
  const [isExtraction, setIsExtraction] = useState(data?.isExtraction || false);
  const [overwrite, setOverwrite] = useState(data?.overwrite || false);
  const [agentSessionMode, setAgentSessionMode] = useState(
    data?.agentSessionMode || false,
  );
  const [sessionSource, setSessionSource] = useState(
    data?.sessionSource || 'generate',
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [settingsAnchorEl, setSettingsAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const openSettingsPopover = Boolean(settingsAnchorEl);

  const [flowIsExtraction, setFlowIsExtraction] = useState(false);
  const [flowTypeOCR, setFlowTypeOCR] = useState('mistral_ocr');
  const [formData, setFormData] = useState({
    label: data?.label || '',
    myId: data?.id || '',
    selectedBy: data?.selectedBy || '',
    selectedFlow: data?.selectedFlow || '',
    collapsed: data?.collapsed || false,
    myType: data?.type || '',
    title: data?.title || '',
    fileLink: data?.fileLink || '',
    text: data?.text || '',
    executionMode: data?.executionMode || 'returnAfterFlow',
    agentSessionMode: data?.agentSessionMode || false,
    sessionSource: data?.sessionSource || 'generate',
    sessionId: data?.sessionId || '',
  });

  const edges = useEdges();

  const [flow, setFlow] = useState<{
    nodes: any[];
    edges: any[];
  }>({
    nodes: [],
    edges: [],
  });

  const { activeSession } = useSessionStore(state => state);

  const getSessionId = () => {
    if (!agentSessionMode) return undefined;

    switch (sessionSource) {
      case 'pass_current':
        return activeSession.id;
      case 'custom':
        const sessionIdData = data?.sessionIdData || [];
        if (sessionIdData.length > 0) {
          const connectedValue = sessionIdData.map((item: any) => item.text || '').join('');
          if (connectedValue) return connectedValue;
        }
        return data.sessionId || 'default';
      case 'generate':
      default:
        return generateRandomId();
    }
  };

  useEffect(() => {
    const getFlows = async () => {
      setIsLoading(true);
      try {
        const response = await FlowService.getFlow(data.selectedFlow);

        if (!selectedFlow) {
          nodeDataAutoSaveDynamic({
            newEdges: edges,
            setNodeState,
            id,
            setSaving,
            flowId,
            objectData: {
              selectedFlow: response.data.id,
            },
            setSchema,
          });
        }

        setFlow(response.data);

        const varInputNodes = response.data?.nodes?.filter(
          (item: { type: string }) => item.type === 'varInputNode',
        );
        if (varInputNodes && varInputNodes.length > 0) {
          const firstVarInput = varInputNodes[0];
          setFlowIsExtraction(firstVarInput.data?.isExtraction || false);
          setFlowTypeOCR(firstVarInput.data?.typeOCR || 'mistral_ocr');
        }
      } catch (error) {
        setNotification({
          type: 'error',
          message: 'Something went wrong',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (data.selectedFlow) {
      getFlows();
    }
  }, [data.selectedFlow]);

  useEffect(() => {
    if (data?.selectedFlow) {
      setSelectedFlow(data.selectedFlow);
    }
  }, [data?.selectedFlow]);

  useEffect(() => {
    if (!isChangingDirectly) {
      setFormData({
        ...data,
      });
    }
  }, [data, isChangingDirectly]);

  useEffect(() => {
    setIsExtraction(data?.isExtraction || false);
    setOverwrite(data?.overwrite || false);
  }, [data?.isExtraction, data?.overwrite]);

  const onClick = async () => {
    setBtnLoading(true);
    try {
      const map = flow?.nodes;
      const varInputNodes = map?.filter(
        (item: { type: string }) => item.type === 'varInputNode',
      );
      const getOutputObjectNode = map?.filter(
        (item: { type: string }) => item.type === 'outputObjectNode',
      );

      const variableInputs = varInputNodes?.map((varInputNode: any) => {
        const varInputId = varInputNode.id;

        const textData = data?.textData?.[varInputId] || [];
        let inputText = '';

        if (textData.length > 0) {
          inputText = textData.map((item: any) => item.text || '').join(' ');
        } else {
          const inputTextKey = `${varInputId}Text`;
          inputText =
            formData[inputTextKey as keyof typeof formData] ||
            data[inputTextKey] ||
            '';
        }

        const linksForThisInput = data?.fileLinkData?.[varInputId] || [];

        const formattedLinks = linksForThisInput.map((linkItem: any) => {
          return {
            name: linkItem.label || '',
            url: linkItem.text || '',
          };
        });

        const allLinks =
          formattedLinks.length > 0
            ? formattedLinks
            : formData[`${varInputId}FileLink` as keyof typeof formData]
            ? [
                {
                  name: formData[
                    `${varInputId}FileLink` as keyof typeof formData
                  ],
                  url: formData[
                    `${varInputId}FileLink` as keyof typeof formData
                  ],
                },
              ]
            : [];

        const files = allLinks
          .filter((link: any) => link.url && link.url.startsWith('data:'))
          .map((link: any) => {
            return {
              name: link.name || 'File name with extension (optional)',
              base64: link.url || 'File content encoded in base64 (optional)',
            };
          });

        const links = allLinks.filter(
          (link: any) => !link.url || !link.url.startsWith('data:'),
        );

        return {
          inputId: varInputId,
          inputText: inputText,
          links,
          files,
          isExtraction: overwrite ? isExtraction : flowIsExtraction,
          typeOCR: overwrite ? typeOCR : flowTypeOCR,
        };
      });

      const sessionIdValue = getSessionId();

      if (formData.executionMode === 'triggerOnly') {
        FluxService.FluxApi(data?.selectedFlow, {
          variableInputs,
          executionMode: 'triggerOnly',
          ...(agentSessionMode
            ? { sessionId: sessionIdValue }
            : {}),
        }).catch(error => {
          console.warn(
            'Agent execution triggered but response ignored:',
            error,
          );
        });

        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          flowTriggerData: checkExecuteTrigger(data, id),
          changeType: 'execute',
          objectCallerData: [
            ...(user?.spaceObjects || []),
            ...(user?.objects || []),
          ],
          flowId,
          objectData: {},
          setSchema,
          setSaving,
        });

        setNotification({
          type: 'success',
          message: 'Agent triggered remotely.',
        });

        setExecuteCounter(0);
        setTimeout(() => {
          setHasToExecute(true);
        }, 1000);

        return;
      }

      const response = await FluxService.FluxApi(data?.selectedFlow, {
        variableInputs,
        executionMode: 'returnAfterFlow',
        ...(agentSessionMode
          ? { sessionId: sessionIdValue }
          : {}),
      });

      if (response.data.message?.length !== 0) {
        const object: { [target: string]: string } = {};

        getOutputObjectNode?.forEach((item: { id: any }) => {
          const output = response.data.message.find(
            (output: { outputId: any }) => output.outputId === item.id,
          );

          if (!object[item.id]) {
            object[item.id] = output.text;
          }

          object[item.id] = output.text;
        });

        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          flowTriggerData: checkExecuteTrigger(data, id),
          changeType: 'execute',
          objectCallerData: [
            ...(user?.spaceObjects || []),
            ...(user?.objects || []),
          ],

          flowId,
          objectData: {
            ...object,
          },
          setSchema,
          setSaving,
        });

        setExecuteCounter(0);

        setTimeout(() => {
          setHasToExecute(true);
        }, 1000);
      }
    } catch (error) {
      executeErrorHandle(
        executeCounter,
        setExecuteCounter,
        setHasToExecute,
        data,
        edges,
        setNodeState,
        setSaving,
        id,
        flowId,
        setSchema,
        setNotification,
        error,
        [...(user?.spaceObjects || []), ...(user?.objects || [])],
      );
      setNotification({
        type: 'error',
        message: 'Something went wrong',
      });
    } finally {
      setBtnLoading(false);
    }
  };

  const debounceTimeoutRef = useRef<any>(null);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (data?.flowTrigger?.id && !data?.canceled && !data?.paused) {
      if (hasToExecute) {
        debounceTimeoutRef.current = setTimeout(() => {
          setHasToExecute(false);
          onClick();
        }, 500);
      }
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [data?.flowTrigger, hasToExecute, data?.canceled, data?.paused]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const setValue = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const onChangeSelect = useCallback(
    (event: { target: { name: string; value: any } }) => {
      if (event.target.name === 'selectedFlow') {
        setSelectedFlow(event.target.value);

        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          objectData: {
            selectedFlow: event.target.value,
          },
          setSchema,
        });
      }
    },
    [id, edges, setNodeState, setSaving, flowId, setSchema],
  );

  const onChangeTagInput = (e: { target: { name: string; value: any } }) => {
    setValue(e.target.name, e.target.value);

    setWhoIsChanging({
      name: e.target.name,
      value: e.target.value,
    });
    setIsChangingDirectly(true);
  };

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        setIsChangingDirectly(false);
        const objectToSave = {
          [whoIsChanging.name]: whoIsChanging.value,
        };

        if (whoIsChanging.name.endsWith('Text')) {
          const varInputId = whoIsChanging.name.replace('Text', '');

          if (data?.textData && data.textData[varInputId]?.length > 0) {
            const updatedTextData = { ...(data.textData || {}) };

            updatedTextData[varInputId] = updatedTextData[varInputId].map(
              (item: any, index: number) => {
                if (index === 0) {
                  return {
                    ...item,
                    text: whoIsChanging.value,
                  };
                }
                return item;
              },
            );

            objectToSave.textData = updatedTextData;
          }
        }

        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          objectData: objectToSave,
          setSchema,
        });

        setWhoIsChanging({
          value: '',
          name: '',
        });
      }
    },
    [isChangingDirectly, whoIsChanging, data],
    300,
  );

  useDebounceEffect(
    () => {
      if (
        (data?.fileLinkData && Object.keys(data.fileLinkData).length > 0) ||
        (data?.textData && Object.keys(data.textData).length > 0)
      ) {
        const objectToSave: any = {};

        if (data?.fileLinkData && Object.keys(data.fileLinkData).length > 0) {
          objectToSave.fileLinkData = data.fileLinkData;
        }

        if (data?.textData && Object.keys(data.textData).length > 0) {
          objectToSave.textData = data.textData;
        }

        if (Object.keys(objectToSave).length > 0) {
          nodeDataAutoSaveDynamic({
            newEdges: edges,
            setNodeState,
            id,
            setSaving,
            flowId,
            objectData: objectToSave,
            setSchema,
          });
        }
      }
    },
    [data?.fileLinkData, data?.textData],
    300,
  );

  const handleRun = (run: string) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      setSaving,
      id,
      flowId,
      objectData: {
        run,
      },
      setSchema,
    });
  };

  const handleExecutionModeChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        [name]: value,
      },
      setSchema,
    });
  };

  const handleOCRTypeChange = (event: any) => {
    const value = event.target.value;
    setTypeOCR(value);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        typeOCR: value,
      },
      setSchema,
    });
  };

  const handleExtractionChange = (event: any) => {
    const value = event.target.checked;
    setIsExtraction(value);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        isExtraction: value,
      },
      setSchema,
    });
  };

  const handleOverwriteChange = (event: any) => {
    const value = event.target.checked;
    setOverwrite(value);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        overwrite: value,
      },
      setSchema,
    });
  };

  const handleOpenPopoverSettings = (event: {
    currentTarget: React.SetStateAction<HTMLButtonElement | null> | any;
  }) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopoverSettings = () => {
    setAnchorEl(null);
  };

  const handleAgentSessionModeChange = (event: any) => {
    const value = event.target.checked;
    setAgentSessionMode(value);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        agentSessionMode: value,
      },
      setSchema,
    });
  };

  const handleSessionSourceChange = (event: any) => {
    const value = event.target.value;
    setSessionSource(value);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      objectData: {
        sessionSource: value,
      },
      setSchema,
    });
  };

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;
  const { user } = useAuthStore(state => state);

  const onChangeOnExecute = (e: any, handleId: string) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      setSaving,
      objectData: {
        clearOnExecution: {
          ...data?.clearOnExecution,
          [handleId]: e.target.checked,
        },
      },
      setSchema,
    });
  };

  return (
    <>
      <Box className={isLoading || btnLoading ? 'node' : ''}>
        <Node
          edges={edges}
          type={type}
          btnText="Run Agent"
          handleRun={handleRun}
          onSubmit={onClick}
          isLoading={isLoading || btnLoading}
          data={data}
          id={id}
          showTokensUsed={false}
        >
          {!data?.collapsed && <></>}

          <Box display={'flex'}>
            <Box
              p={'16px'}
              borderRight={`1px solid ${
                user?.theme === 'dark' ? '#475467' : '#EAECF0'
              }`}
            >
              <Box
                display={'flex'}
                gap={4}
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <Label
                  isAdvanced={true}
                  sx={{ mb: '0' }}
                  labelName="Select Agent to Run "
                />

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <IconButtonTooltip
                    title="Filter Settings"
                    onClick={event => setSettingsAnchorEl(event.currentTarget)}
                  >
                    <Gear
                      size={22}
                      color={openSettingsPopover ? '#3650DE' : '#98A2B3'}
                    />
                  </IconButtonTooltip>

                  <Popover
                    open={openSettingsPopover}
                    anchorEl={settingsAnchorEl}
                    id="simple-popover"
                    onClose={() => setSettingsAnchorEl(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <Box
                      sx={{
                        width: '350px',
                        border: '1px solid var(--Neutral-300, #D0D5DD)',
                        padding: '16px',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '22px',
                      }}
                    >
                      <Box>
                        <Label
                          isAdvanced={true}
                          labelName="Agent Execution Mode"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.executionMode === 'triggerOnly'}
                              onChange={e =>
                                handleExecutionModeChange(
                                  'executionMode',
                                  e.target.checked
                                    ? 'triggerOnly'
                                    : 'returnAfterFlow',
                                )
                              }
                              name="executionMode"
                            />
                          }
                          label={
                            formData.executionMode === 'triggerOnly'
                              ? 'Trigger Only'
                              : 'Return after Agent'
                          }
                          sx={{
                            color:
                              user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                            '& .MuiFormControlLabel-label': {
                              fontSize: '14px',
                            },
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              user?.theme === 'dark' ? '#98A2B3' : '#667085',
                            fontSize: '12px',
                            mt: '4px',
                          }}
                        >
                          {formData.executionMode === 'triggerOnly'
                            ? 'Executes the agent and does not return the output to this agent'
                            : 'Executes the agent and returns the output to continue this agent'}
                        </Typography>
                      </Box>

                      <Box>
                        <Label
                          sx={{ mb: '0' }}
                          isAdvanced={true}
                          labelName="Agent Session Mode"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={agentSessionMode}
                              onChange={handleAgentSessionModeChange}
                              name="agentSessionMode"
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                sx={{
                                  color: '#475467',
                                  fontWeight: '400',
                                  fontSize: '12px',
                                }}
                              >
                                {agentSessionMode
                                  ? 'Agent Session Mode Enabled'
                                  : 'Agent Session Mode Disabled'}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>

                      {agentSessionMode && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          sx={{ opacity: agentSessionMode ? 1 : 0.5 }}
                        >
                          <Typography
                            sx={{
                              color: '#475467',
                              fontWeight: '600',
                              fontSize: '14px',
                              mb: '8px',
                            }}
                          >
                            Session ID Source Options
                          </Typography>

                          <RadioGroup
                            aria-labelledby="demo-session-id-source-label"
                            value={sessionSource}
                            name="session-id-source-options"
                            onChange={handleSessionSourceChange}
                          >
                            <FormControlLabel
                              value="generate"
                              control={<Radio disabled={!agentSessionMode} />}
                              disabled={!agentSessionMode}
                              label={
                                <Box>
                                  <Typography
                                    sx={{
                                      color: '#475467',
                                      fontWeight: '600',
                                      fontSize: '14px',
                                    }}
                                  >
                                    Generate New Session ID
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: '#475467',
                                      fontWeight: '400',
                                      fontSize: '12px',
                                    }}
                                  >
                                    Randomly generates unique session ID. <br />
                                    Creates isolated session context for the
                                    called agent.
                                  </Typography>
                                </Box>
                              }
                            />

                            <FormControlLabel
                              value="pass_current"
                              control={<Radio disabled={!agentSessionMode} />}
                              disabled={!agentSessionMode}
                              label={
                                <Box>
                                  <Typography
                                    sx={{
                                      color: '#475467',
                                      fontWeight: '600',
                                      fontSize: '14px',
                                    }}
                                  >
                                    Pass Current Session ID
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: '#475467',
                                      fontWeight: '400',
                                      fontSize: '12px',
                                    }}
                                  >
                                    Forwards the existing session ID from the
                                    calling agent. <br />
                                    Maintains session continuity across agent
                                    calls.
                                    <br />
                                    If calling agent has no session ID,
                                    generates new one.
                                  </Typography>
                                </Box>
                              }
                            />

                            <FormControlLabel
                              value="custom"
                              control={<Radio disabled={!agentSessionMode} />}
                              disabled={!agentSessionMode}
                              label={
                                <Box>
                                  <Typography
                                    sx={{
                                      color: '#475467',
                                      fontWeight: '600',
                                      fontSize: '14px',
                                    }}
                                  >
                                    Custom Session ID
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: '#475467',
                                      fontWeight: '400',
                                      fontSize: '12px',
                                    }}
                                  >
                                    User provides specific session ID value
                                    (manual input or dynamic connection). <br />
                                    Allows manual session management and
                                    linking.
                                  </Typography>
                                </Box>
                              }
                            />
                          </RadioGroup>
                        </Box>
                      )}
                    </Box>
                  </Popover>
                </Box>
              </Box>

              {flows?.length !== 0 && (
                <Select
                  value={selectedFlow ?? 'Select Agent'}
                  name="selectedFlow"
                  className="nodrag"
                  onChange={onChangeSelect}
                  disabled={btnLoading}
                  size="small"
                  sx={{
                    '& .MuiList-root': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                    },
                    '& .MuiSvgIcon-root': {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: 'none',
                    '.MuiOutlinedInput-notchedOutline': { border: 0 },
                    bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .Mui-selected': {
                          backgroundColor:
                            user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                        },
                        backgroundColor:
                          user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      },
                    },
                  }}
                >
                  <MenuItem
                    disabled
                    value={'Select Agent'}
                    sx={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                    }}
                  >
                    <em>Select Agent</em>
                  </MenuItem>

                  <MenuItem
                    disabled
                    value={'Spaces Agent'}
                    sx={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                    }}
                  >
                    <em>Private Agents</em>
                  </MenuItem>

                  {flows
                    ?.filter(flow => !flow?.spaceName)
                    ?.sort((a, b) => (a?.name > b?.name ? 1 : -1))
                    .map((flow, i) => (
                      <MenuItem
                        key={i}
                        value={flow.id}
                        sx={{
                          color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                        }}
                      >
                        {flow.name}
                      </MenuItem>
                    ))}

                  {flows?.filter(flow => flow?.spaceName)?.length !== 0 && (
                    <MenuItem
                      disabled
                      value={'Spaces Agent'}
                      sx={{
                        color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                      }}
                    >
                      <em>Space Agents</em>
                    </MenuItem>
                  )}
                  {flows
                    ?.filter(flow => flow?.spaceName)
                    ?.sort((a, b) => (a?.name > b?.name ? 1 : -1))
                    .map((flow, i) => (
                      <MenuItem
                        key={i}
                        value={flow.id}
                        sx={{
                          color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                        }}
                      >
                        {flow.name}
                      </MenuItem>
                    ))}
                </Select>
              )}

              <Box mt={'16px'}>
                {flow?.nodes
                  ?.filter(
                    (item: { type: string }) => item.type === 'varInputNode',
                  )
                  ?.map((varInputNode: any, idx: any) => (
                    <Box key={idx}>
                      <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                        <Label
                          isAdvanced={true}
                          labelName={varInputNode.data.label}
                          sx={{ marginBottom: '20px' }}
                        />

                        {varInputNode.data.isExtraction ? (
                          <IconButtonTooltip
                            sx={{ marginBottom: '20px' }}
                            title="Convert files to text: (Extracts and processes the text from the file)"
                          >
                            <Info size={16} color="#101828" />
                          </IconButtonTooltip>
                        ) : (
                          <IconButtonTooltip
                            sx={{ marginBottom: '20px' }}
                            title="Pass file URL: (Sends the file URL without converting the contents)"
                          >
                            <Info size={16} color="#101828" />
                          </IconButtonTooltip>
                        )}
                      </Box>

                      <TagInput
                        labelName="Data"
                        handleId={`${varInputNode?.id}Text`}
                        name={`${varInputNode?.id}Text`}
                        dataConnected={data?.textData?.[varInputNode?.id] || []}
                        value={
                          formData[
                            `${varInputNode?.id}Text` as keyof typeof formData
                          ]
                        }
                        isActive={isActiveEdge(
                          edges,
                          id,
                          `${varInputNode?.id}Text`,
                          'target',
                        )}
                        onChange={onChangeTagInput}
                        isAdvanced={true}
                        findColor={findColor}
                        advancedCollapsed={true}
                        isCollapsed={true}
                        maxHeight={320}
                        maxGrowHeight={320}
                        nodeId={id}
                        removeIcons={true}
                        nodeLabel={data?.label}
                        placeholder={'Information goes here'}
                      />

                      {(varInputNode.data.isShowFile) && (
                        <TagInput
                          labelName="File"
                          handleId={`${varInputNode?.id}FileLink`}
                          name={`${varInputNode?.id}FileLink`}
                          dataConnected={
                            data?.fileLinkData?.[varInputNode?.id] || []
                          }
                          value={
                            formData[
                              `${varInputNode?.id}FileLink` as keyof typeof formData
                            ] || ''
                          }
                          isActive={isActiveEdge(
                            edges,
                            id,
                            `${varInputNode?.id}FileLink`,
                            'source',
                          )}
                          onChange={onChangeTagInput}
                          onSettings={handleOpenPopoverSettings}
                          isAdvanced={true}
                          findColor={findColor}
                          advancedCollapsed={true}
                          isCollapsed={true}
                          maxHeight={320}
                          maxGrowHeight={320}
                          nodeId={id}
                          removeIcons={true}
                          nodeLabel={data?.label}
                          placeholder={'Links goes here'}
                          hasSettings={true}
                        />
                      )}

                      <Divider
                        orientation="horizontal"
                        style={{ marginBottom: '16px' }}
                      />
                    </Box>
                  ))}
              </Box>

              {agentSessionMode && sessionSource === 'custom' && (
                <Box mt={2}>
                  <TagInput
                    labelName="Session ID"
                    handleId="sessionId"
                    name="sessionId"
                    dataConnected={data?.sessionIdData || []}
                    value={formData.sessionId || ''}
                    isActive={isActiveEdge(edges, id, 'sessionId', 'target')}
                    onChange={onChangeTagInput}
                    isAdvanced={true}
                    findColor={findColor}
                    advancedCollapsed={true}
                    isCollapsed={true}
                    maxHeight={320}
                    maxGrowHeight={320}
                    nodeId={id}
                    removeIcons={true}
                    nodeLabel={data?.label}
                    placeholder={'Enter session ID or connect...'}
                  />
                </Box>
              )}
            </Box>

            <Box p={'16px 24px 16px 16px'} sx={{ height: '100%' }}>
              {!flow?.nodes.filter(
                (item: { type: string }) => item.type === 'outputObjectNode',
              ).length && (
                <Box
                  width={'360px'}
                  height={'170px'}
                  sx={{
                    padding: '16px',
                    borderRadius: '8px',
                    backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%2398A2B3FF' stroke-width='2' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '10px',
                      flexDirection: 'column',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    <FlowArrow size={32} color="#98A2B3" />
                    <Typography fontSize={'14px'} color="#98A2B3">
                      The output(s) from the agent you run will appear here.
                    </Typography>
                  </Box>
                </Box>
              )}
              {flow?.nodes?.length > 0 &&
                flow?.nodes
                  .filter(
                    (item: { type: string }) =>
                      item.type === 'outputObjectNode',
                  )
                  .reverse()
                  .map((varInputNode: any, idx: number) => (
                    <OutputTextarea
                      key={idx}
                      maxOutputHeight={0}
                      previewIndex={0}
                      value={
                        formData?.[varInputNode?.id as keyof typeof formData]
                      }
                      activeHandle={isActiveEdge(
                        edges,
                        id,
                        varInputNode?.id,
                        'source',
                      )}
                      placement={data?.placement}
                      labelName={varInputNode.data.label}
                      nodeLabel={data?.label}
                      type={data.myId}
                      handleId={varInputNode?.id}
                      hasClearOnExecute={true}
                      onChangeOnExecute={onChangeOnExecute}
                      clearOnExecution={
                        data?.clearOnExecution?.[varInputNode?.id] || false
                      }
                      flowCaller={true}
                    />
                  ))}
            </Box>
          </Box>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClosePopoverSettings}
            PaperProps={{
              sx: {
                backgroundColor: user?.theme === 'dark' ? '#101828' : '#FFFFFF',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                padding: '16px',
              },
            }}
          >
            <Box display="flex" width="100%" justifyContent="end">
              <XCircle
                size={24}
                onClick={handleClosePopoverSettings}
                style={{ cursor: 'pointer' }}
              />
            </Box>

            <Box display="flex" flexDirection="column" gap="24px">
              <FormControl
                sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
              >
                <Box display="flex" gap="8px" alignItems="center">
                  <Gear color="#101828" size={24} />
                  <FormLabel sx={{ color: '#101828' }}>
                    File Processing Settings
                  </FormLabel>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={overwrite}
                      onChange={handleOverwriteChange}
                      name="overwrite"
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        sx={{
                          color: '#475467',
                          fontWeight: '600',
                          fontSize: '14px',
                        }}
                      >
                        Override Agent Settings
                      </Typography>
                      <Typography
                        sx={{
                          color: '#475467',
                          fontWeight: '400',
                          fontSize: '12px',
                        }}
                      >
                        {overwrite
                          ? 'Use custom settings for this node'
                          : 'Use default settings from the selected agent'}
                      </Typography>
                    </Box>
                  }
                />

                <Box sx={{ opacity: overwrite ? 1 : 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={overwrite ? isExtraction : flowIsExtraction}
                        onChange={handleExtractionChange}
                        name="isExtraction"
                        disabled={!overwrite}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          sx={{
                            color: '#475467',
                            fontWeight: '600',
                            fontSize: '14px',
                          }}
                        >
                          Enable Data Extraction
                        </Typography>
                        <Typography
                          sx={{
                            color: '#475467',
                            fontWeight: '400',
                            fontSize: '12px',
                          }}
                        >
                          {(overwrite ? isExtraction : flowIsExtraction)
                            ? 'Extracts and processes the text from the file'
                            : 'Sends the file URL without converting the contents'}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

                {(overwrite ? isExtraction : flowIsExtraction) && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{ opacity: overwrite ? 1 : 0.5 }}
                  >
                    <Typography
                      sx={{
                        color: '#475467',
                        fontWeight: '600',
                        fontSize: '14px',
                        mb: '8px',
                      }}
                    >
                      OCR Type
                    </Typography>

                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      value={overwrite ? typeOCR : flowTypeOCR}
                      name="radio-buttons-group"
                      onChange={handleOCRTypeChange}
                    >
                      <FormControlLabel
                        value="mistral_ocr"
                        control={<Radio disabled={!overwrite} />}
                        disabled={!overwrite}
                        label={
                          <Typography
                            sx={{
                              color: '#475467',
                              fontWeight: '600',
                              fontSize: '14px',
                            }}
                          >
                            Mistral OCR
                          </Typography>
                        }
                      />

                      <FormControlLabel
                        value="mistral_vision"
                        control={<Radio disabled={!overwrite} />}
                        disabled={!overwrite}
                        label={
                          <>
                            <Typography
                              sx={{
                                color: '#475467',
                                fontWeight: '600',
                                fontSize: '14px',
                              }}
                            >
                              Vision OCR
                            </Typography>
                            <Typography
                              sx={{
                                color: '#475467',
                                fontWeight: '400',
                                fontSize: '14px',
                              }}
                            >
                              (Recommended to use for more complex images)
                            </Typography>
                          </>
                        }
                      />

                      <FormControlLabel
                        value="google_ocr"
                        control={<Radio disabled={!overwrite} />}
                        disabled={!overwrite}
                        label={
                          <>
                            <Typography
                              sx={{
                                color: '#475467',
                                fontWeight: '600',
                                fontSize: '14px',
                              }}
                            >
                              Google OCR
                            </Typography>
                            <Typography
                              sx={{
                                color: '#475467',
                                fontWeight: '400',
                                fontSize: '14px',
                              }}
                            >
                              (Recommended for general files)
                            </Typography>
                          </>
                        }
                      />
                    </RadioGroup>
                  </Box>
                )}
              </FormControl>

              <Button
                onClick={() => {
                  handleClosePopoverSettings();
                }}
                variant="contained"
              >
                Save
              </Button>
            </Box>
          </Popover>
        </Node>
      </Box>
    </>
  );
};

export default memo(FlowCaller);
